/* eslint-disable jsx-a11y/no-interactive-element-to-noninteractive-role --
   The table is laid out with `display: grid` (CMS-1772 R2), and Chrome and
   WebKit drop the native table semantics whenever `display` is overridden. The
   role below looks redundant against the markup but is what keeps the table in
   the accessibility tree, so it must stay. */
import classNames from 'classnames';
import {
  formatPremiumAmount,
  premiumCurrency,
} from '../../../calculatedAttributes/providers/tiQuote';
import { CalculatedAttributeResult } from '../../../calculatedAttributes/resolve';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import { CELL_PADDING } from '../constants';
import { TRANSLATIONS } from '../i18n';

interface PriceCellProps {
  /** The engine's answer for this row: loader, price, or dash. */
  result: CalculatedAttributeResult<'TI_QUOTE'>;
}

/**
 * Value cell for the calculated `TI_PREMIUM_PRICE` attribute (CMS-1896 R5).
 *
 * A pure formatter: prices are resolved upstream and never authored. While the
 * quote poll is still running an unquoted cell shows a skeleton; once settled,
 * a missing quote renders "-" and leaves the row visible.
 */
export const PriceCell = ({ result }: PriceCellProps) => {
  const price = result.state === 'resolved' ? result.value : undefined;
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  // Both halves or neither: a badge with nothing struck through is worse than
  // the plain price.
  const hasDiscount =
    price?.originalAmount !== undefined && price?.discountPercent !== undefined;

  // Formatting and the currency fallback live with the type, so every surface
  // showing a premium shows it identically.
  const currency = premiumCurrency(price, context.currencyDisplay);

  return (
    <td
      role="cell"
      className={classNames(
        // No brand tint — the Price column deliberately follows the default
        // column style, unlike Hot Reward Pick (CMS-1896 R3).
        'bg-fill/card group-hover/row:bg-fill/base',
        'border-l border-t border-borders/default',
        'min-h-table/cell-min-height',
        CELL_PADDING,
        // `overflow-hidden` as `ValueCell` and `IdentityCell` carry it, and this
        // cell needs it most: the discount badge is `text-nowrap` inside a Price
        // track that is fixed at every breakpoint (R7), so a long percentage plus
        // a struck-through 7-digit original cannot wrap out of trouble and the
        // track cannot grow. Without it the overflow paints over the column
        // beside it.
        'break-words overflow-hidden',
      )}
    >
      {result.state === 'pending' ? (
        // Same skeleton idiom as PlanTierSelector's price placeholders: two
        // pulsing bars sized to the settled layout (amount line + discount
        // line), so the fill-in does not shift the row height.
        <div className="flex h-full flex-col justify-center gap-spacing/gap/soft-6px">
          <span className="h-6 w-20 animate-pulse rounded-radius/button bg-fill/alternative-3" />
          <span className="h-3 w-14 animate-pulse rounded-radius/button bg-fill/alternative-3" />
        </div>
      ) : price ? (
        <div className="flex h-full flex-col justify-center">
          <div className="flex items-baseline gap-0.5">
            <span className="font-font-weight/caption text-font-size/caption leading-line-height/caption text-text/secondary-lighter">
              {currency}
            </span>
            <span className="font-font-weight/title-2 text-font-size/title-2 leading-line-height/title-2 text-text/primary">
              {formatPremiumAmount(price.amount)}
            </span>
          </div>
          {hasDiscount && (
            <div className="flex flex-wrap items-center gap-spacing/gap/soft-6px">
              <span className="font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell text-text/secondary-lighter line-through">
                {currency}
                {formatPremiumAmount(price.originalAmount!)}
              </span>
              <div className="text-nowrap rounded-radius/tooltip px-spacing/padding/soft-8px py-spacing/padding/soft-4px bg-highlights/green-light text-highlights/green-dark">
                <span className="font-font-weight/table-cell-strong text-font-size/table-cell leading-line-height/table-cell">
                  {/* Figma renders both markets number-first and spaced —
                      "25% off" and "40% 折扣" — so the label simply trails the
                      percentage. Note this differs from ProductCard's TI
                      section, which puts the ZH label in front. */}
                  {price.discountPercent}% {staticText.discount_off}
                </span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex h-full flex-col justify-center">
          <span className="font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell text-text/primary">
            -
          </span>
        </div>
      )}
    </td>
  );
};
